import React from 'react';

import { ValueGroup } from '@candu-ai/craft/ui';
import {
  FontFamily,
  FontWeight,
  LetterSpacing,
  LineHeight,
  TextAlignment,
  TextColor,
  TextSize,
  TextStyle,
} from '@candu-ai/editor-nodes/text-settings';
import { BuiltInComponents } from 'craft/constants';
import { Section } from 'craft/containers/Toolbox/components';
import { useCss, useMultiNodes, useTypographyNodeMarks } from 'craft/hooks';
import { CssSelectorId } from 'craft/hooks/useMultiCssSelector';
import { CommonNodeProperties } from 'craft/types';

interface Props {
  nodeIds: string[];
}

export const TypographyStyles = ({ nodeIds }: Props) => {
  const { isRootViewportCss, css, setCss, stylesHierarchy } = useCss(nodeIds, {
    commonProperty: CommonNodeProperties.Multiple,
  });
  const {
    type,
    custom: { variant },
  } = useMultiNodes(nodeIds, (node) => ({
    custom: node?.data.custom?.custom ?? {},
  }));

  const { css: linkCss, setCss: setLinkCss, stylesHierarchy: linkStylesHierarchy } = useCss(
    nodeIds,
    {
      cssSelectorId: CssSelectorId.TypographyHrefChildren,
      commonProperty: CommonNodeProperties.Multiple,
    },
  );

  const { marks, toggleMark } = useTypographyNodeMarks();

  return (
    <>
      <Section name="Text Styles">
        <ValueGroup>
          <FontFamily
            cssProps={css}
            setCss={setCss}
            stylesHierarchy={stylesHierarchy}
            variant={variant}
            isInGroup={{ isFirst: true }}
          />
          <TextSize
            cssProps={css}
            setCss={setCss}
            stylesHierarchy={stylesHierarchy}
            variant={variant}
            isInGroup
          />
          <FontWeight
            cssProps={css}
            setCss={setCss}
            stylesHierarchy={stylesHierarchy}
            variant={variant}
            isInGroup
          />
          {type === BuiltInComponents.Typography && (
            <>
              <LineHeight
                cssProps={css}
                setCss={setCss}
                stylesHierarchy={stylesHierarchy}
                variant={variant}
                isInGroup
              />
              <LetterSpacing
                cssProps={css}
                setCss={setCss}
                stylesHierarchy={stylesHierarchy}
                variant={variant}
                isInGroup
              />
            </>
          )}
          <TextAlignment
            cssProps={css}
            setCss={setCss}
            stylesHierarchy={stylesHierarchy}
            isInGroup={{ isLast: !isRootViewportCss }}
          />
          {isRootViewportCss && (
            <>
              {type === BuiltInComponents.Typography && (
                <TextStyle
                  marks={marks}
                  toggleMark={toggleMark}
                  isInGroup
                  stylesHierarchy={stylesHierarchy}
                />
              )}
              <TextColor
                label="Text Color"
                cssProps={css}
                setCss={setCss}
                stylesHierarchy={stylesHierarchy}
                isInGroup={type === BuiltInComponents.Typography ? true : { isLast: true }}
              />
              {type === BuiltInComponents.Typography && (
                <TextColor
                  label="Link Color"
                  cssProps={linkCss}
                  setCss={setLinkCss}
                  stylesHierarchy={linkStylesHierarchy}
                  isInGroup={{ isLast: true }}
                />
              )}
            </>
          )}
        </ValueGroup>
      </Section>
    </>
  );
};

TypographyStyles.craft = {
  isResponsiveStyle: true,
};
